/**
 * The notifications panel (design-reference §7.9): the recent-notifications list
 * (read/unread, deep links, mark-all-read) with a preferences view toggled in
 * place. Self-contained and driven by the real API — the shell mounts the bell
 * that opens it.
 */
import { useState } from 'react';

import { Button, EmptyState, Skeleton, Toggle } from '../../components';
import type { NotificationView } from '../../api/schema';

import { usePreferences, type NotificationsState } from './useNotifications';
import { NOTIFICATION_TYPES, TYPE_LABEL, targetFor, timeAgo } from './labels';

import './notifications.css';

function PreferencesView(): React.JSX.Element {
  const { status, prefs, toggle } = usePreferences();

  if (status === 'loading') {
    return (
      <div className="ntf-prefs" aria-busy="true">
        <Skeleton height={44} />
        <Skeleton height={44} />
      </div>
    );
  }
  if (status === 'error') {
    return <p className="ntf-empty-note">Couldn&rsquo;t load your notification preferences.</p>;
  }

  return (
    <div className="ntf-prefs">
      <p className="ntf-prefs__intro">Choose which notifications reach you, per channel.</p>
      <table className="ntf-prefs__table">
        <thead>
          <tr>
            <th scope="col">Notification</th>
            <th scope="col">In-app</th>
            <th scope="col">Email</th>
          </tr>
        </thead>
        <tbody>
          {NOTIFICATION_TYPES.map((type) => (
            <tr key={type}>
              <th scope="row" className="ntf-prefs__name">
                {TYPE_LABEL[type]}
              </th>
              <td>
                <Toggle
                  label={`${TYPE_LABEL[type]} in-app`}
                  checked={!prefs.muted_in_app.includes(type)}
                  onChange={() => {
                    toggle('in_app', type);
                  }}
                />
              </td>
              <td>
                <Toggle
                  label={`${TYPE_LABEL[type]} email`}
                  checked={!prefs.muted_email.includes(type)}
                  onChange={() => {
                    toggle('email', type);
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface NotificationsPanelProps {
  state: NotificationsState;
  /** Navigate to a deep link (and close the panel). */
  onNavigate: (path: string) => void;
}

export function NotificationsPanel({ state, onNavigate }: NotificationsPanelProps): React.JSX.Element {
  const [view, setView] = useState<'list' | 'prefs'>('list');
  const { status, items, unread, markRead, markAllRead } = state;

  const onItemClick = (n: NotificationView): void => {
    if (!n.read) markRead(n.id);
    onNavigate(targetFor(n));
  };

  return (
    <div className="ntf-panel" role="dialog" aria-label="Notifications">
      <header className="ntf-panel__head">
        <h2 className="ntf-panel__title">
          Notifications
          {unread > 0 ? <span className="ntf-panel__count">{unread}</span> : null}
        </h2>
        <div className="ntf-panel__actions">
          {view === 'list' ? (
            <>
              <button
                type="button"
                className="ntf-linkbtn"
                onClick={() => {
                  void markAllRead();
                }}
                disabled={unread === 0}
              >
                Mark all read
              </button>
              <button
                type="button"
                className="ntf-iconbtn"
                aria-label="Notification preferences"
                onClick={() => {
                  setView('prefs');
                }}
              >
                ⚙
              </button>
            </>
          ) : (
            <button
              type="button"
              className="ntf-linkbtn"
              onClick={() => {
                setView('list');
              }}
            >
              ← Back
            </button>
          )}
        </div>
      </header>

      {view === 'prefs' ? (
        <PreferencesView />
      ) : status === 'loading' ? (
        <div className="ntf-list" aria-busy="true">
          <Skeleton height={56} />
          <Skeleton height={56} />
          <Skeleton height={56} />
        </div>
      ) : status === 'error' ? (
        <div className="ntf-empty">
          <EmptyState
            title="Couldn't load notifications"
            body="Something went wrong fetching your notifications."
            actions={
              <Button variant="secondary" onClick={state.reload}>
                Retry
              </Button>
            }
          />
        </div>
      ) : items.length === 0 ? (
        <div className="ntf-empty">
          <EmptyState
            title="You're all caught up"
            body="New review backlogs, category suggestions, processing errors and cleanup summaries will show up here."
          />
        </div>
      ) : (
        <ul className="ntf-list">
          {items.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                className={n.read ? 'ntf-item' : 'ntf-item ntf-item--unread'}
                onClick={() => {
                  onItemClick(n);
                }}
              >
                <span className="ntf-item__dot" aria-hidden="true" />
                <span className="ntf-item__body">
                  <span className="ntf-item__title">
                    {n.title}
                    {!n.read ? <span className="sr-only"> (unread)</span> : null}
                  </span>
                  <span className="ntf-item__text">{n.body}</span>
                  <span className="ntf-item__meta">
                    <span className="ntf-item__type">{TYPE_LABEL[n.type]}</span>
                    <span className="ntf-item__time">{timeAgo(n.created_at)}</span>
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
